import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";

const QUICK_TABLE = [
  { topic: "State sales tax nexus rules", acronym: "—", covered: true, note: "YES (48 states + DC — 50 total)" },
  { topic: "State consumer privacy laws", acronym: "—", covered: true, note: "YES (15 states with active numeric scoring)" },
  { topic: "Federal children's privacy", acronym: "COPPA", covered: false, note: "NO" },
  { topic: "FTC deceptive design rules", acronym: "FTC", covered: false, note: "NO" },
  { topic: "Web accessibility", acronym: "ADA", covered: false, note: "NO" },
  { topic: "State & federal data breach laws", acronym: "varies", covered: false, note: "NO — all 50 states" },
  { topic: "International privacy", acronym: "GDPR", covered: false, note: "NO" },
  { topic: "Industry federal rules", acronym: "HIPAA/TTB/FDA", covered: false, note: "NO" },
  { topic: "Commercial email marketing", acronym: "CAN-SPAM", covered: false, note: "NO" },
  { topic: "City and county regulations", acronym: "—", covered: false, note: "NO" },
];

const NOT_COVERED_STATES = [
  "Connecticut (CT)", "Delaware (DE)", "Massachusetts (MA)", "Maryland (MD)",
  "Montana (MT)", "New Hampshire (NH)", "New Jersey (NJ)", "Ohio (OH)",
  "Oregon (OR)", "Pennsylvania (PA)", "Rhode Island (RI)", "Utah (UT)",
];

const CARDS = [
  {
    heading: "COPPA — Federal Children's Privacy",
    body:
      "The Children's Online Privacy Protection Act applies to websites directed to children under 13 or that knowingly collect personal data from children under 13. Merchants selling toys, children's clothing, games, educational products, or goods marketed to minors should seek separate professional guidance. Regulens does not assess COPPA applicability.",
    links: [
      { label: "FTC COPPA guidance", url: "https://www.ftc.gov/business-guidance/privacy-security/childrens-privacy" },
    ],
  },
  {
    heading: "FTC — Deceptive Design Enforcement",
    body:
      "The Federal Trade Commission actively pursues websites using misleading countdown timers, false scarcity claims, hidden subscription fees, pre-checked add-ons, and other deceptive interface patterns. These practices may also trigger enforcement under state consumer protection laws in several covered jurisdictions. Regulens does not audit website design for FTC compliance.",
    links: [{ label: "FTC business guidance", url: "https://www.ftc.gov/business-guidance" }],
  },
  {
    heading: "ADA — Web Accessibility",
    body:
      "The Americans with Disabilities Act has been applied to commercial websites in multiple federal court decisions. ADA demand letters targeting small e-commerce businesses are an active enforcement pattern in the United States. Regulens does not scan your website's front-end code, alt-text, or screen-reader compatibility. Regulens analyzes transaction volume data and policy text only. Accessibility review requires a separate technical audit of your store's front-end code.",
    links: [{ label: "ADA guidance", url: "https://www.ada.gov" }],
  },
  {
    heading: "State & Federal Data Breach Notification",
    body:
      "All 50 U.S. states — including the 50 tax jurisdictions and 15 privacy-scored states covered by Regulens — have enacted separate data breach notification statutes. Regulens coverage of a state for sales tax or consumer privacy does NOT extend to that state's data breach notification requirements. These laws are entirely outside Regulens scope regardless of which states a merchant sells in. Most require notification within 30 to 72 hours of discovery.",
    links: [
      { label: "FTC data security resources", url: "https://www.ftc.gov/business-guidance/privacy-security" },
      { label: "State attorney general guidelines", url: "https://www.naag.org" },
    ],
  },
  {
    heading: "GDPR — International Privacy",
    body:
      "If a merchant sells to EU customers, the General Data Protection Regulation applies regardless of U.S. state law coverage. GDPR has its own consent requirements, data subject rights, and enforcement regime. Regulens covers U.S. jurisdictions only.",
    links: [{ label: "GDPR information", url: "https://gdpr.eu" }],
  },
  {
    heading: "CAN-SPAM — Commercial Email Marketing",
    body:
      "The CAN-SPAM Act governs commercial email marketing to U.S. recipients. Requirements include a functional unsubscribe mechanism, accurate sender identification, and non-deceptive subject lines. Fines reach $50,120 per email in violation. Regulens does not audit email marketing compliance.",
    links: [
      {
        label: "FTC CAN-SPAM guide",
        url: "https://www.ftc.gov/business-guidance/resources/can-spam-act-compliance-guide-business",
      },
    ],
  },
  {
    heading: "Industry-Specific Federal Rules",
    body:
      "Healthcare products: HIPAA may apply. Financial services or installment payments: Gramm-Leach-Bliley Act may apply. Alcohol sales: Federal TTB regulations apply. Food and supplements: FDA labeling rules apply. Regulens does not assess industry-specific federal frameworks.",
    links: [],
  },
  {
    heading: "Municipal and County Regulations",
    body:
      "Regulens covers state-level rules only. City, county, and municipal ordinances are entirely outside scope. Colorado is a notable example where home-rule municipalities set their own sales tax rules separately from state rules.",
    links: [{ label: "Colorado home-rule information", url: "https://tax.colorado.gov/home-rule-localities" }],
  },
];

export default function FederalScopeNotice() {
  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-12" data-testid="federal-scope-notice-page">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Coverage Scope</div>
        <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)] mb-4">
          What Regulens Covers and What It Does Not
        </h1>

        <div
          className="mb-10 rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 text-sm text-[var(--rg-amber)]"
          data-testid="scope-disclaimer"
        >
          This page is an informational scope disclosure only. It does not constitute legal advice. Consult a licensed attorney for guidance on any of these areas.
        </div>

        {/* QUICK TABLE */}
        <h2 className="font-heading text-xl text-[var(--rg-text-strong)] mb-3">At a Glance</h2>
        <div className="rounded-md border border-[var(--rg-border)] overflow-hidden mb-6">
          <table className="w-full text-sm" data-testid="scope-quick-table">
            <thead>
              <tr className="bg-[var(--rg-surface)] text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)]">
                <th className="text-left px-4 py-3">Topic</th>
                <th className="text-left px-4 py-3">Acronym</th>
                <th className="text-left px-4 py-3">Covered by Regulens</th>
              </tr>
            </thead>
            <tbody>
              {QUICK_TABLE.map((row) => (
                <tr key={row.topic} className="border-t border-[var(--rg-border)]/60">
                  <td className="px-4 py-3 text-[var(--rg-text)]">{row.topic}</td>
                  <td className="px-4 py-3 text-[var(--rg-muted)] font-mono text-xs">{row.acronym}</td>
                  <td className="px-4 py-3">
                    <span className={row.covered ? "text-[var(--rg-sage)] font-semibold" : "text-[var(--rg-red)] font-semibold"}>
                      {row.note}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* NOT COVERED JURISDICTIONS BOX */}
        <div
          className="rounded-md border border-[var(--rg-red)]/40 bg-[var(--rg-red)]/5 p-5 mb-12"
          data-testid="not-covered-states-box"
        >
          <h3 className="font-heading font-bold text-[var(--rg-text-strong)] mb-2">States Not Included in Regulens v4.0</h3>
          <p className="text-sm text-[var(--rg-text)] mb-3">
            The following 12 U.S. states are not included in the current Regulens edition:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 text-sm text-[var(--rg-text)] mb-3">
            {NOT_COVERED_STATES.map((s) => (
              <div key={s}>{s}</div>
            ))}
          </div>
          <p className="text-sm text-[var(--rg-text)]">
            For live verification status of all covered jurisdictions, see the{" "}
            <Link to="/data-verification" className="text-[var(--rg-sage)] hover:underline">
              Data Verification page
            </Link>
            .
          </p>
        </div>

        {/* SECTION 1 */}
        <h2 className="font-heading text-2xl text-[var(--rg-text-strong)] mb-3">What Regulens Covers</h2>
        <div className="text-sm text-[var(--rg-text)] leading-relaxed space-y-3 mb-12">
          <p>Regulens Version 4.0 provides informational readiness indicators for:</p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              State-level sales tax economic nexus rules across 50 U.S. jurisdictions (48 states + DC). Every finding traces to an official state revenue department or enacted statute.
            </li>
            <li>
              State-level consumer privacy laws with active numeric scoring for 15 states (rows tracked for all 39 U.S. jurisdictions with an enacted or emerging privacy law). Every finding traces to an official state attorney general office or enacted statute.
            </li>
          </ol>
          <p>12 U.S. states are not included. See above.</p>
        </div>

        {/* SECTION 2 */}
        <h2 className="font-heading text-2xl text-[var(--rg-text-strong)] mb-2">Important: What Regulens Does Not Cover</h2>
        <p className="text-sm text-[var(--rg-text)] mb-6">
          The following areas are outside the scope of Regulens and require separate professional review. This list is not exhaustive.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {CARDS.map((card, i) => (
            <div
              key={card.heading}
              className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5"
              data-testid={`scope-card-${i}`}
            >
              <h3 className="font-heading font-bold text-[var(--rg-text-strong)] text-sm mb-2">{card.heading}</h3>
              <p className="text-xs text-[var(--rg-text)] leading-relaxed mb-3">{card.body}</p>
              {card.links.length > 0 && (
                <ul className="text-xs space-y-1">
                  {card.links.map((l) => (
                    <li key={l.url}>
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1"
                      >
                        {l.label} <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-[var(--rg-muted)] italic">
          This scope disclosure was last updated June 2026. Regulens reviews this page periodically. This page is informational only. Always consult a licensed professional.
        </p>
      </section>
    </Layout>
  );
}
